/**
 * Signing in through RigoHR (ADR-0059). The consoles have no login page of their own in a deployed
 * environment: with no session, the browser goes to `/bff/handoff/start`, which sends it to RigoHR
 * and back to `/bff/handoff`, and lands it on the return path with the cookie set. A refused or
 * failed handoff lands on `/login?handoff=denied|failed|unavailable`; signing out lands on
 * `/login?signedOut=1`. Both stop the automatic hand-off, so neither loops straight back in.
 *
 * No design-system import here: the documentation reader's sign-in gate uses this too, and the
 * reader never downloads RDS.
 */

export const LOGIN_PATH = "/login";

/**
 * Where a handoff started from `/login` returns, and where `/login` sends someone already signed
 * in. The ticket console, not `/`: on the console hostname `/` is the documentation index.
 */
export const CONSOLE_HOME = "/console";

const HANDOFF_START = "/bff/handoff/start";

/** Where the browser goes. An object, so a test can spy on it: jsdom's `location` cannot be. */
export const browser = {
  assign: (url: string) => window.location.assign(url),
  replace: (url: string) => window.location.replace(url),
};

type Place = { pathname: string; search: string };

/** The full-page navigation that starts a handoff, returning to `from`. */
export function handoffStartUrl(from: Place) {
  // Never back to the login route or its query: that would show the refusal again after success.
  const onLogin = from.pathname === LOGIN_PATH || from.pathname.startsWith(`${LOGIN_PATH}/`);
  const back = onLogin ? CONSOLE_HOME : `${from.pathname}${from.search}`;
  return `${HANDOFF_START}?returnUrl=${encodeURIComponent(back)}`;
}

export function startHandoff(from: Place) {
  browser.assign(handoffStartUrl(from));
}

/** Sign-out lands here, so the page says so rather than signing straight back in through RigoHR. */
export function signedOut() {
  browser.replace(`${LOGIN_PATH}?signedOut=1`);
}

const FAILED = "Sign-in didn't complete. Try again.";
const NOTICES: Record<string, string> = {
  denied:
    "Your account doesn't have access to this console. It is for Rigo staff with an active RigoHR account and a role here.",
  failed: FAILED,
  unavailable: "Sign-in is unavailable right now. Try again in a moment.",
};

export type HandoffNotice = { tone: "danger" | "warning" | "info"; text: string };

/** What the URL says about the last handoff, or null when there is nothing to say. */
export function handoffNotice(search: string): HandoffNotice | null {
  const query = new URLSearchParams(search);
  const outcome = query.get("handoff");
  if (outcome !== null) {
    return { tone: outcome === "denied" ? "danger" : "warning", text: NOTICES[outcome] ?? FAILED };
  }
  if (query.get("signedOut") === "1") return { tone: "info", text: "You've signed out." };
  return null;
}

// The loop guard. A handoff that comes back without a session (a cookie the browser refused, say)
// would otherwise start another at once, forever. Once per page load, and not again within a minute
// of the last one in this tab: the second time the person gets the button instead.
const STAMP = "rigo.support.handoff.started";
const WINDOW_MS = 60_000;
let startedThisLoad = false;

/** Whether the console may hand off without being asked. Reads only; `claimAutoHandoff` records. */
export function mayAutoHandoff(now: number = Date.now()): boolean {
  if (startedThisLoad) return false;
  try {
    const last = Number(window.sessionStorage.getItem(STAMP));
    return !(Number.isFinite(last) && last > 0 && now - last < WINDOW_MS);
  } catch {
    return true;
  }
}

/** True when the console may hand off without being asked; records that it is about to. */
export function claimAutoHandoff(now: number = Date.now()): boolean {
  if (!mayAutoHandoff(now)) return false;
  try {
    window.sessionStorage.setItem(STAMP, String(now));
  } catch {
    // Storage blocked: the per-load guard still holds.
  }
  startedThisLoad = true;
  return true;
}

/** Tests only: forget that this "page load" already handed off. */
export function resetAutoHandoff() {
  startedThisLoad = false;
  try {
    window.sessionStorage.removeItem(STAMP);
  } catch {
    // Nothing to clear.
  }
}
